import { useEffect, useState, type ReactElement } from 'react';
import { AlertCircle, GitBranch, Loader2, X } from 'lucide-react';
import { labApi } from '@/lib/api';
import { AvatarPicker, VoicePicker, type ProductionAvatarOption, type ProductionVoiceOption } from './ProductionPickers';

// Modal "Iteruj wariant": nowa treść na bazie istniejącego skryptu wideo.
// Skrypt: zostaw / parafrazuj / nowy hook; opcjonalnie produkcja z tym samym
// lub nowym głosem i avatarem (winning ad → wariant z innym aktorem).

type ScriptMode = 'keep' | 'paraphrase' | 'rehook';
type SameOrNew = 'same' | 'new';

interface IterateVariantModalProps {
  productId: string;
  contentId: string;
  contentTitle: string;
  contentType: string;
  strategy: string | null;
  onStarted: () => void;
  onClose: () => void;
}

const SCRIPT_MODE_OPTIONS: Array<{ mode: ScriptMode; label: string; description: string }> = [
  {
    mode: 'keep',
    label: 'Zostaw oryginalny',
    description: 'Skrypt 1:1. Gotowe zasoby tekstowe (sceny, b-rolle, brief) zostaną skopiowane - idealne do wymiany samego aktora lub głosu.',
  },
  {
    mode: 'paraphrase',
    label: 'Parafrazuj',
    description: 'Ten sam sens, struktura i timing, ale inne brzmienie. Łańcuch zasobów wygeneruje się od nowa.',
  },
  {
    mode: 'rehook',
    label: 'Nowy hook',
    description: 'Wymienia otwarcie (pierwsze 3-6 s) na inny mechanizm zaczepienia, reszta skryptu bez zmian. Łańcuch od nowa.',
  },
];

export function IterateVariantModal({ productId, contentId, contentTitle, contentType, strategy, onStarted, onClose }: IterateVariantModalProps) {
  const [scriptMode, setScriptMode] = useState<ScriptMode>('paraphrase');
  const [instruction, setInstruction] = useState('');
  const [profile, setProfile] = useState<string>('none');
  const [previousProduction, setPreviousProduction] = useState<{ voiceId: string | null; avatarId: string | null } | null>(null);
  const [productionEnabled, setProductionEnabled] = useState(false);
  const [voiceChoice, setVoiceChoice] = useState<SameOrNew>('same');
  const [avatarChoice, setAvatarChoice] = useState<SameOrNew>('same');
  const [voices, setVoices] = useState<ProductionVoiceOption[]>([]);
  const [avatars, setAvatars] = useState<ProductionAvatarOption[]>([]);
  const [voiceId, setVoiceId] = useState('');
  const [avatarId, setAvatarId] = useState('');
  const [loadingContext, setLoadingContext] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [starting, setStarting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Kontekst: profil produkcji strategii + parametry ostatniej produkcji oryginału
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [strategiesRes, assetsRes] = await Promise.all([
          labApi.getVideoStrategies(),
          labApi.getVideoAssets(productId, contentId),
        ]);
        if (cancelled) return;
        const strategies = strategiesRes.data.data?.strategies || [];
        const strategyKey = (strategy || '').trim().toUpperCase();
        setProfile(strategies.find((item: { key: string }) => item.key === strategyKey)?.productionProfile
          || (contentType === 'UGC_SCRIPT' ? 'heygen' : 'none'));
        const assets = (assetsRes.data.data || []) as Array<{ metadata?: { kind?: string; voiceId?: string; avatarId?: string } | null; createdAt: string }>;
        const lastProduction = assets
          .filter(asset => asset.metadata?.kind === 'VIDEO_MAIN' && (asset.metadata?.voiceId || asset.metadata?.avatarId))
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        if (lastProduction) {
          setPreviousProduction({
            voiceId: lastProduction.metadata?.voiceId || null,
            avatarId: lastProduction.metadata?.avatarId || null,
          });
        } else {
          setVoiceChoice('new');
          setAvatarChoice('new');
        }
      } catch (err) {
        console.error('Failed to load variant context:', err);
      } finally {
        if (!cancelled) setLoadingContext(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productId, contentId, strategy, contentType]);

  const provider = profile === 'heygen' ? 'heygen' : 'elevenlabs';
  const needsAvatar = profile === 'heygen';
  const needsVoiceSelect = voiceChoice === 'new';
  const needsAvatarSelect = needsAvatar && avatarChoice === 'new';
  const needsOptions = productionEnabled && (needsVoiceSelect || needsAvatarSelect);

  // Głosy/avatary pobieramy dopiero gdy user faktycznie wybiera "nowy"
  useEffect(() => {
    if (!needsOptions || optionsLoaded || loadingOptions) return;
    let cancelled = false;
    setLoadingOptions(true);
    (async () => {
      try {
        const res = await labApi.getVideoProductionOptions(provider);
        if (cancelled) return;
        const data = res.data.data || {};
        setVoices(data.voices || []);
        setAvatars(data.avatars || []);
        if ((data.voices || []).length > 0) setVoiceId(data.voices[0].voiceId);
        if ((data.avatars || []).length > 0) setAvatarId(data.avatars[0].avatarId);
        setOptionsLoaded(true);
      } catch (err: any) {
        if (!cancelled) setModalError(err?.response?.data?.error || 'Nie udało się pobrać opcji produkcji. Sprawdź integrację w Integracje → Produkcja reklam AI.');
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();
    return () => { cancelled = true; };
  }, [needsOptions, optionsLoaded, loadingOptions, provider]);

  const resolvedVoiceId = voiceChoice === 'same' ? previousProduction?.voiceId || '' : voiceId;
  const resolvedAvatarId = avatarChoice === 'same' ? previousProduction?.avatarId || '' : avatarId;
  const productionInvalid = productionEnabled && (
    !resolvedVoiceId || (needsAvatar && !resolvedAvatarId) || (needsOptions && !optionsLoaded)
  );

  const handleStart = async () => {
    setModalError(null);
    setStarting(true);
    try {
      await labApi.iterateVideoVariant(productId, contentId, {
        scriptMode,
        instruction: instruction.trim() || undefined,
        production: productionEnabled
          ? { voiceId: resolvedVoiceId || undefined, avatarId: needsAvatar ? resolvedAvatarId || undefined : undefined }
          : null,
      });
      onStarted();
    } catch (err: any) {
      setModalError(err?.response?.data?.error || 'Nie udało się uruchomić iteracji wariantu.');
      setStarting(false);
    }
  };

  const sameOrNewPicker = (args: {
    label: string;
    choice: SameOrNew;
    onChoice: (value: SameOrNew) => void;
    sameAvailable: boolean;
    picker: ReactElement;
  }) => (
    <div>
      <span className="text-xs font-medium text-text-secondary mb-1.5 block">{args.label}</span>
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => args.onChoice('same')}
          disabled={!args.sameAvailable}
          title={args.sameAvailable ? undefined : 'Oryginał nie miał jeszcze produkcji - wybierz z listy'}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
            args.choice === 'same'
              ? 'text-accent-primary bg-accent-primary/10 border-accent-primary/30'
              : 'text-text-secondary bg-surface-hover/40 border-border hover:border-border-strong'
          }`}
        >
          Ten sam
        </button>
        <button
          type="button"
          onClick={() => args.onChoice('new')}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
            args.choice === 'new'
              ? 'text-accent-primary bg-accent-primary/10 border-accent-primary/30'
              : 'text-text-secondary bg-surface-hover/40 border-border hover:border-border-strong'
          }`}
        >
          Nowy
        </button>
      </div>
      {args.choice === 'new' && (
        loadingOptions ? (
          <div className="flex items-center gap-2 py-1 text-xs text-text-muted">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Pobieranie listy...
          </div>
        ) : args.picker
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-raised rounded-xl border border-border w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-semibold text-text-primary flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-accent-primary" /> Iteruj wariant
          </h3>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors" aria-label="Zamknij">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-text-muted mb-4">
          Nowa wersja treści <span className="text-text-secondary font-medium">{contentTitle}</span> - wybierz co bierzemy z oryginału, a co zmieniamy. Zasoby produkcyjne przygotują się automatycznie.
        </p>

        {loadingContext ? (
          <div className="flex items-center gap-2 py-6 text-xs text-text-muted">
            <Loader2 className="w-4 h-4 animate-spin" /> Ładowanie danych oryginału...
          </div>
        ) : (
          <div className="space-y-5">
            {/* SKRYPT */}
            <div>
              <span className="text-xs font-semibold text-text-primary uppercase tracking-wider mb-2 block">Skrypt</span>
              <div className="space-y-2">
                {SCRIPT_MODE_OPTIONS.map(option => (
                  <button
                    key={option.mode}
                    type="button"
                    onClick={() => setScriptMode(option.mode)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      scriptMode === option.mode
                        ? 'border-accent-primary/40 bg-accent-primary/5'
                        : 'border-border bg-surface-hover/30 hover:border-border-strong'
                    }`}
                  >
                    <span className={`text-xs font-medium block ${scriptMode === option.mode ? 'text-accent-primary' : 'text-text-primary'}`}>
                      {option.label}
                    </span>
                    <span className="text-[11px] text-text-muted block mt-0.5">{option.description}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* WYTYCZNE */}
            <div>
              <label htmlFor="variant-instruction" className="text-xs font-medium text-text-secondary mb-1.5 block">
                Wytyczne iteracji (opcjonalnie)
              </label>
              <textarea
                id="variant-instruction"
                value={instruction}
                onChange={e => setInstruction(e.target.value)}
                placeholder="np. hook oparty o liczbę zamiast pytania, cieplejszy ton, nacisk na gwarancję..."
                rows={2}
                className="w-full text-xs bg-surface-hover border border-border rounded-lg px-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
              />
            </div>

            {/* PRODUKCJA */}
            {profile !== 'none' && (
              <div>
                <span className="text-xs font-semibold text-text-primary uppercase tracking-wider mb-2 block">Produkcja</span>
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={productionEnabled}
                    onChange={e => setProductionEnabled(e.target.checked)}
                    className="mt-0.5 accent-[var(--accent-primary,#4a9eff)]"
                  />
                  <span>
                    <span className="text-xs font-medium text-text-primary block">
                      Uruchom produkcję {profile === 'heygen' ? 'wideo (HeyGen)' : 'audio (ElevenLabs)'} automatycznie
                    </span>
                    <span className="text-[11px] text-text-muted block mt-0.5">
                      Po przygotowaniu zasobów plik wygeneruje się i trafi na Google Drive bez dalszego klikania.
                    </span>
                  </span>
                </label>

                {productionEnabled && (
                  <div className="mt-3 space-y-3 pl-1">
                    {needsAvatar && sameOrNewPicker({
                      label: 'Aktor (avatar)',
                      choice: avatarChoice,
                      onChoice: setAvatarChoice,
                      sameAvailable: Boolean(previousProduction?.avatarId),
                      picker: <AvatarPicker idPrefix="variant" avatars={avatars} value={avatarId} onChange={setAvatarId} />,
                    })}
                    {sameOrNewPicker({
                      label: 'Głos',
                      choice: voiceChoice,
                      onChoice: setVoiceChoice,
                      sameAvailable: Boolean(previousProduction?.voiceId),
                      picker: <VoicePicker idPrefix="variant" voices={voices} value={voiceId} onChange={setVoiceId} />,
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {modalError && (
          <div className="flex items-center gap-2 mt-3 text-[11px] text-red-400">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {modalError}
          </div>
        )}

        <div className="flex items-center justify-between mt-5">
          <button
            onClick={handleStart}
            disabled={starting || loadingContext || productionInvalid}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
          >
            {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitBranch className="w-3.5 h-3.5" />}
            Utwórz wariant
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors"
          >
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
